<template>
  <div
    class="dropdown topnav-notifications-control"
    :class="{ open: isOpen }">
    <div
      href="#"
      class="dropdown-toggle display-flex profiledrop-avatar btn btn-icon scale-28 p-0"
      :class="$route.path === '/message' ? 'btn-primary' : 'btn-default'"
      :aria-expanded="String(isOpen)"
      aria-label="Open notifications"
      @click.prevent="togglePanel">
      <span
        class="mdi scale-20"
        :class="$route.path === '/message' ? 'mdi-bell' : 'mdi-bell-outline'"
        aria-hidden="true" />
      <span
        v-if="unreadCount > 0"
        class="img-circle topnav-notification-dot"
        aria-hidden="true" />
    </div>
    <div
      v-if="isOpen"
      class="dropdown-menu topnav-notifications-menu bg-white flex-direction-column"
      role="region"
      aria-label="Notifications">
      <div class="topnav-notifications-header display-flex align-items-center ga-2 pa-12p flex-none">
        <strong class="fs-16 fw-7">Notifications</strong>
        <span class="badge topnav-notifications-total bg-white fs-12 lh-14">{{ totalCount }}</span>
        <span class="badge topnav-notifications-unread text-white fs-12 lh-14">{{ unreadCount }} Unread</span>
        <a
          class="topnav-notifications-view-all fs-12 fw-5 lh-20 text-nowrap"
          href="/message">View All</a>
      </div>
      <div
        class="topnav-notifications-body panel-body scrollable pa-12p flex-fill"
        @click.capture.prevent.stop
        @keydown.enter.capture.prevent.stop>
        <div
          v-if="isLoading && !messages.length"
          class="text-xs-center pa-3">
          <v-progress-circular
            color="secondary"
            size="40"
            indeterminate />
        </div>
        <div
          v-else-if="!messages.length"
          class="text-center">
          No notifications yet.
        </div>
        <div
          v-for="message in messages"
          :key="message.id">
          <MessageBlockQuote
            :message="message" />
          <div
            :key="`${message.id}-breaker`"
            v-observe-visibility="(isVisible) => visibilityChanged(isVisible, message)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { EventBus } from '@/helpers'
import MessageBlockQuote from '@/components/common/MessageBlockQuote'

export default {
  name       : 'NotificationsPanel',
  components : { MessageBlockQuote },
  data() {
    return {
      isOpen : false
    }
  },
  computed : {
    ...mapState('notifications', [
      'messages', 'totalCount', 'unreadCount', 'isLoading', 'cacheKey'
    ])
  },
  watch : {
    '$user.id'() {
      this.initialize()
    },
    '$company.id'() {
      this.initialize()
    }
  },
  created() {
    this.readTimers = new Map()
    this.readingMessageIds = new Set()
    EventBus.$on('notifications-refresh', this.onNotificationEvent)
  },
  mounted() {
    this.fallbackBell = document.getElementById('notification-bell-fallback')
    if (this.fallbackBell) {
      this.fallbackBell.hidden = true
    }
    document.addEventListener('click', this.closeOnOutsideClick)
    this.logoutLink = document.querySelector('a.logout')
    if (this.logoutLink) {
      this.logoutLink.addEventListener('click', this.clearCache)
    }
    window.addEventListener('focus', this.refresh)
    window.addEventListener('storage', this.syncStorage)
    this.unsubscribe = this.$store.subscribe(mutation => {
      if (mutation.type === 'dashboard/UPDATE_MESSAGE_READ_SUCCESS') {
        this.syncRead(mutation.payload.message)
      }
    })
    this.initialize()
  },
  beforeDestroy() {
    if (this.fallbackBell) {
      this.fallbackBell.hidden = false
    }
    this.clearReadTimers()
    EventBus.$off('notifications-refresh', this.onNotificationEvent)
    document.removeEventListener('click', this.closeOnOutsideClick)
    if (this.logoutLink) {
      this.logoutLink.removeEventListener('click', this.clearCache)
    }
    window.removeEventListener('focus', this.refresh)
    window.removeEventListener('storage', this.syncStorage)
    this.unsubscribe()
  },
  methods : {
    ...mapActions('notifications', {
      initializeNotifications : 'initialize',
      refreshNotifications    : 'refresh',
      markNotificationRead    : 'markRead',
      syncRead                : 'syncRead',
      syncFromStorage         : 'syncFromStorage',
      clearCache              : 'clearCache'
    }),
    initialize() {
      if (this.$user && this.$user.id && this.$company && this.$company.id) {
        this.initializeNotifications({
          userId    : this.$user.id,
          companyId : this.$company.id
        })
      }
    },
    refresh() {
      return this.refreshNotifications()
    },
    onNotificationEvent() {
      this.refreshNotifications({ force : true })
    },
    syncStorage(event) {
      if (event.key === this.cacheKey) {
        this.syncFromStorage()
      }
    },
    togglePanel() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.refresh()
      } else {
        this.clearReadTimers()
      }
    },
    closeOnOutsideClick(event) {
      if (this.isOpen && !this.$el.contains(event.target)) {
        this.isOpen = false
        this.clearReadTimers()
      }
    },
    clearReadTimers() {
      this.readTimers.forEach(timer => window.clearTimeout(timer))
      this.readTimers.clear()
    },
    visibilityChanged(isVisible, message) {
      window.clearTimeout(this.readTimers.get(message.id))
      this.readTimers.delete(message.id)

      const currentMessage = this.messages.find(item => item.id === message.id)
      if (isVisible && currentMessage && !currentMessage.isSeen && !this.readingMessageIds.has(message.id)) {
        const timer = window.setTimeout(() => {
          this.readTimers.delete(message.id)
          this.markRead(message.id)
        }, 1000)
        this.readTimers.set(message.id, timer)
      }
    },
    async markRead(messageId) {
      const message = this.messages.find(item => item.id === messageId)

      if (!this.isOpen || !message || message.isSeen || this.readingMessageIds.has(messageId)) {
        return
      }
      this.readingMessageIds.add(messageId)
      try {
        await this.markNotificationRead(messageId)
      } finally {
        this.readingMessageIds.delete(messageId)
      }
    }
  }
}

if (window.SP && window.SP.EventManager) {
  window.SP.EventManager.on({
    messageSaved : function (message) {
      if (message && message.type === 'NOTIFICATION' &&
        Number(message.companyId) === Number(window.SP.Scenario.Company.id)) {
        EventBus.$emit('notifications-refresh')
      }
    },
    messageDeleted : function (message) {
      if (message && message.type === 'NOTIFICATION' &&
        Number(message.companyId) === Number(window.SP.Scenario.Company.id)) {
        EventBus.$emit('notifications-refresh')
      }
    },
    messagereadSaved : function (read) {
      if (read && Number(read.userId) === Number(window.SP.Scenario.User.id)) {
        EventBus.$emit('notifications-refresh')
      }
    },
    messagereadDeleted : function (read) {
      if (read && Number(read.userId) === Number(window.SP.Scenario.User.id)) {
        EventBus.$emit('notifications-refresh')
      }
    }
  })
}
</script>
